import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { ICost } from '@leedpulse-monorepo/interfaces';
import { CONTACTS_PAGE_PATH } from '../../../pages/contacts/ContactsPage';
import { currencyAsString, CostTypeRef, creditAdjustmentReasonAsString, destinationLineTypeAsString } from '@leedpulse-monorepo/enums';
import { Tooltip } from 'antd';

const formatNumber = (value: number, round = 2): number => Math.round((value ?? 0) * 10 ** round) / 10 ** round;

const countryNames = new Intl.DisplayNames(['fr'], { type: 'region' });

// What the client pays: the credits value, or the money amount of a destination-priced call
const clientAmount = (record: ICost): number | null => record.billing?.amount ?? record.credit_value ?? null;

const destinationDetails = (record: ICost): string => {
  const billing = record.billing;
  if (!billing) return '';
  const country = countryNames.of(billing.country) ?? billing.country;
  return `${country} · ${destinationLineTypeAsString(billing.line_type)} · ${billing.units} min x ${formatNumber(billing.unit_price, 3)} ${currencyAsString(billing.currency)}/min · ${billing.status}`;
};

export const costsColumns: ColumnsType<ICost> = [
  {
    title: 'Créé le',
    dataIndex: 'created',
    key: 'created',
    render: (created: number) => (created ? new Date(created).toLocaleString('fr-FR') : '-'),
  },
  {
    title: 'Fournisseur',
    dataIndex: 'provider',
    key: 'provider',
  },
  {
    title: 'Modèle',
    dataIndex: 'model',
    key: 'model',
  },
  {
    title: 'Type de coût',
    dataIndex: 'type_ref',
    key: 'type_ref',
    render: (type_ref: string | null) => type_ref ?? '-',
  },
  {
    title: 'Unités',
    dataIndex: 'units',
    key: 'units',
    render: (units: number, record: ICost) => `${formatNumber(units)} ${record.units_type ?? ''}`,
  },
  {
    title: 'Mouvement crédits',
    dataIndex: 'credit_consumed',
    key: 'credit_consumed',
    render: (credit_consumed: number | null | undefined, record: ICost) => {
      if (record.billing) {
        return <Tooltip title={destinationDetails(record)}><span style={{ color: 'orange' }}>Hors forfait</span></Tooltip>;
      }
      if (credit_consumed == null) return '-';
      if (record.type_ref === CostTypeRef.CREDITS_RENEWAL) {
        return <span style={{ color: 'green' }}>Renouvelé</span>;
      }
      if (record.type_ref === CostTypeRef.PROMOTIONAL_CREDITS_RENEWAL) {
        return <span style={{ color: 'green' }}>Renouvelé (promotion)</span>;
      }
      // Manual adjustment: signed, with the reason the client sees (who did it is in Référence)
      if (record.type_ref === CostTypeRef.CREDITS_ADJUSTMENT) {
        const delta = -credit_consumed;
        return (
          <span style={{ color: delta > 0 ? 'green' : 'red' }}>
            {delta > 0 ? '+' : ''}{formatNumber(delta)} · {creditAdjustmentReasonAsString(record.model)}
          </span>
        );
      }
      // Positive = consumption (débit), negative = ajout de crédits
      if (credit_consumed > 0) return <span style={{ color: 'red' }}>-{formatNumber(credit_consumed)}</span>;
      if (credit_consumed < 0) return <span style={{ color: 'green' }}>+{formatNumber(Math.abs(credit_consumed))}</span>;
      return formatNumber(credit_consumed);
    },
  },
  {
    title: 'Montant client',
    dataIndex: 'credit_value',
    key: 'credit_value',
    render: (_: number | null, record: ICost) => {
      const amount = clientAmount(record);
      if (amount == null) return '-';
      const label = `${formatNumber(amount, 3)} ${currencyAsString(record.currency)}`;
      return record.billing ? <Tooltip title={destinationDetails(record)}>{label}</Tooltip> : label;
    },
  },
  {
    title: 'Coût',
    dataIndex: 'price',
    key: 'price',
    render: (price: number, record: ICost) => `${formatNumber(price, 3)} ${currencyAsString(record.currency)}`,
  },
  {
    title: 'Marge',
    dataIndex: 'margin',
    key: 'margin',
    render: (_, record: ICost) => {
      const amount = clientAmount(record);
      if (amount === null || record.price === null) return '-'; // Don't show margin if we don't have any value

      const margin = formatNumber(amount - (record.price ?? 0), 3);
      return (
        <span style={{ color: margin > 0 ? 'green' : margin < 0 ? 'red' : 'orange' }}>
          {margin} {currencyAsString(record.currency)}
        </span>
      );
    },
  },
  {
    title: 'Contact',
    key: 'contact',
    render: (_: unknown, record: ICost) => {
      const name = `${record.contact_firstname ?? ''} ${record.contact_lastname ?? ''}`.trim();
      if (!record.id_contact || !name) return '-';
      return <Tooltip title={`${record.id_contact}`}>
        <Link to={`${CONTACTS_PAGE_PATH}/${record.id_contact}`}>{name}</Link>
      </Tooltip>;
    },
  },
  {
    title: 'Référence',
    key: 'reference',
    render: (_: unknown, record: ICost) => {
      if (!record.id_ref) return '-';
      if (record.id_ref === record.id_contact) {
        return <Link to={`${CONTACTS_PAGE_PATH}/${record.id_ref}`}>Voir</Link>;
      }
      return record.id_ref;
    },
  },
];
